<template>
  <v-navigation-drawer
    permanent
    :width="railWidth"
    :mini-variant="!isRailExpanded"
    :mini-variant-width="60"
    color="transparent"
    class="expandable-rail"
    id="expandable-rail"
    :style="railStyle"
    @mouseleave.native="handleMouseLeave"
  >
    <!-- Main Navigation Content -->
    <div class="rail-content">
      <!-- ═══════════════════════════════════════════════════════════════
           CONTEXT NAVIGATION SECTION
           Smart Back + Legacy buttons positioned ABOVE main groups for prominence
           ═══════════════════════════════════════════════════════════════ -->
      <div v-if="showAnyContextNav" class="context-nav-section">

        <!-- Smart Back Row (when we have history) -->
        <div v-if="showSmartBack" class="group-section smart-nav-item">
          <div
            class="smart-nav-row"
            :class="{ 'expanded': isRailExpanded }"
          >
            <!-- History Context Menu (right-click on Smart Back) -->
            <v-menu
              v-model="showHistoryMenu"
              :position-x="historyMenuX"
              :position-y="historyMenuY"
              absolute
              offset-y
              :close-on-content-click="true"
            >
              <v-list dense dark class="history-context-menu">
                <v-list-item
                  v-for="entry in backHistoryEntries"
                  :key="entry.actualIndex"
                  @click="navigateToHistoryEntry(entry)"
                >
                  <v-list-item-icon style="margin-right: 8px;">
                    <v-icon small>{{ getHistoryEntryIcon(entry) }}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ entry.label }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-menu>

            <!-- PRIMARY: Smart Back Button (follows history chain) -->
            <v-tooltip v-if="showSmartBack" right :open-delay="300" :disabled="isRailExpanded">
              <template v-slot:activator="{ on, attrs }">
                <div
                  v-bind="attrs"
                  v-on="on"
                  class="smart-back-btn"
                  :class="{
                    'expanded': isRailExpanded,
                    'is-viewer': backIsViewer,
                    'is-nonviewer': backIsNonViewer
                  }"
                  role="button"
                  :aria-label="backLabel"
                  @click="navigateHistoryBack"
                  @contextmenu.prevent="openHistoryContextMenu($event)"
                >
                  <div class="group-icon">
                    <v-icon :size="24">{{ backIsViewer ? 'mdi-cube-scan' : 'mdi-arrow-left-circle' }}</v-icon>
                  </div>
                  <span v-if="isRailExpanded" class="group-title text-truncate">{{ backLabel }}</span>
                </div>
              </template>
              <span>{{ backLabel }} (Right-click for history)</span>
            </v-tooltip>
          </div>
        </div>

        <!-- Fallback: Back to Viewer - When NOT on MainView AND Smart Back doesn't go to Viewer -->
        <div v-if="!isOnMainView && !isViewerModeActive && !backGoesToViewer" class="group-section">
          <v-tooltip right :open-delay="300" :disabled="isRailExpanded">
            <template v-slot:activator="{ on, attrs }">
              <div
                v-bind="attrs"
                v-on="on"
                class="group-row back-to-viewer-btn context-nav-btn"
                :class="{ 'expanded': isRailExpanded }"
                role="button"
                aria-label="Back to Viewer"
                @click="navigateToMainView"
              >
                <div class="group-icon">
                  <v-icon :size="24">mdi-cube-scan</v-icon>
                </div>
                <span v-if="isRailExpanded" class="group-title">Back to Viewer</span>
              </div>
            </template>
            <span>Back to Viewer</span>
          </v-tooltip>
        </div>

        <!-- Back to Last Location (VIEWERS_MODE) -->
        <div v-if="isOnMainView && isViewerModeActive" class="group-section">
          <v-tooltip right :open-delay="300" :disabled="isRailExpanded">
            <template v-slot:activator="{ on, attrs }">
              <div
                v-bind="attrs"
                v-on="on"
                class="group-row back-to-location-btn context-nav-btn"
                :class="{ 'expanded': isRailExpanded }"
                role="button"
                :aria-label="backToLocationLabel"
                @click="navigateToLastLocation"
              >
                <div class="group-icon">
                  <v-icon :size="24">mdi-arrow-left-circle</v-icon>
                </div>
                <span v-if="isRailExpanded" class="group-title">{{ backToLocationLabel }}</span>
              </div>
            </template>
            <span>{{ backToLocationLabel }}</span>
          </v-tooltip>
        </div>

        <!-- Back to 360 (Nearby 360 fullscreen point cloud mode) -->
        <div v-if="isInPointCloudFullscreen" class="group-section">
          <v-tooltip right :open-delay="300" :disabled="isRailExpanded">
            <template v-slot:activator="{ on, attrs }">
              <div
                v-bind="attrs"
                v-on="on"
                class="group-row back-to-location-btn context-nav-btn"
                :class="{ 'expanded': isRailExpanded }"
                role="button"
                aria-label="Back to 360 View"
                @click="exitPointCloudFullscreen"
              >
                <div class="group-icon">
                  <v-icon :size="24">mdi-arrow-left-circle</v-icon>
                </div>
                <span v-if="isRailExpanded" class="group-title">Back to 360 View</span>
              </div>
            </template>
            <span>Back to 360 View</span>
          </v-tooltip>
        </div>

        <!-- End Mode (VIEWERS_MODE) -->
        <div v-if="isOnMainView && isViewerModeActive" class="group-section">
          <v-tooltip right :open-delay="300" :disabled="isRailExpanded">
            <template v-slot:activator="{ on, attrs }">
              <div
                v-bind="attrs"
                v-on="on"
                class="group-row end-mode-btn context-nav-btn"
                :class="{ 'expanded': isRailExpanded }"
                role="button"
                aria-label="End Mode"
                @click="exitViewerMode"
              >
                <div class="group-icon">
                  <v-icon :size="24">mdi-close-circle</v-icon>
                </div>
                <span v-if="isRailExpanded" class="group-title">End Mode</span>
              </div>
            </template>
            <span>End Mode - Stay in viewer</span>
          </v-tooltip>
        </div>

      </div>

      <!-- Main Groups (filtered by permission - empty groups are hidden) -->
      <div class="groups-container">
        <template v-for="group in visibleMainGroups">
          <div :key="group.id" class="group-section">
            <!-- Group Header Row (Icon + Title) -->
            <v-tooltip
              right
              :open-delay="300"
              :disabled="isRailExpanded"
            >
              <template v-slot:activator="{ on, attrs }">
                <div
                  v-bind="attrs"
                  v-on="on"
                  class="group-row"
                  :class="{
                    'active': activeGroup === group.id,
                    'has-open-panel': activeGroup === group.id && hasPanels,
                    'expanded': isRailExpanded
                  }"
                  role="button"
                  :aria-expanded="activeGroup === group.id && isRailExpanded"
                  :aria-controls="`rail-menu-${group.id}`"
                  :aria-label="`${group.title} menu`"
                  :tabindex="focusedIndex === visibleMainGroups.indexOf(group) ? 0 : -1"
                  @click="handleGroupClick(group.id)"
                  @keydown="handleKeyDown($event, visibleMainGroups.indexOf(group))"
                >
                  <!-- Icon -->
                  <div class="group-icon">
                    <v-icon :size="24">{{ group.icon }}</v-icon>
                  </div>
                  <!-- Title (visible when expanded) -->
                  <span v-if="isRailExpanded" class="group-title">{{ group.title }}</span>
                  <!-- Expand Arrow -->
                  <v-icon
                    v-if="isRailExpanded && group.items.length"
                    class="expand-arrow"
                    :class="{ 'rotated': activeGroup === group.id }"
                    size="16"
                  >
                    mdi-chevron-down
                  </v-icon>
                </div>
              </template>
              <span>{{ group.title }}</span>
            </v-tooltip>

            <!-- Menu Items (shown when this group is active) -->
            <transition name="menu-expand">
              <div
                v-if="isRailExpanded && activeGroup === group.id"
                class="menu-items"
                :id="`rail-menu-${group.id}`"
                role="menu"
              >
                <div
                  v-for="item in getGroupItems(group.id)"
                  :key="item.id"
                  class="menu-item"
                  :class="{
                    'active': isItemActive(item.id),
                    'disabled': isItemDisabled(item)
                  }"
                  role="menuitem"
                  @click="!isItemDisabled(item) && handleItemClick({ itemId: item.id, item })"
                >
                  <v-icon :size="18" class="menu-item-icon">{{ item.icon }}</v-icon>
                  <span class="menu-item-title">{{ item.title }}</span>
                </div>
              </div>
            </transition>
          </div>
        </template>
      </div>

      <!-- Footer Groups (Settings) -->
      <div class="footer-groups">
        <!-- View Layout - ONLY on MainView (3D Viewer) -->
        <div v-if="isOnMainView" class="group-section">
          <v-tooltip right :open-delay="300" :disabled="isRailExpanded">
            <template v-slot:activator="{ on, attrs }">
              <div
                v-bind="attrs"
                v-on="on"
                class="group-row"
                :class="{ 'expanded': isRailExpanded }"
                role="button"
                aria-label="View Layout menu"
              >
                <div class="group-icon">
                  <ViewerDisplayOptions compact />
                </div>
                <span v-if="isRailExpanded" class="group-title">View Layout</span>
              </div>
            </template>
            <span>View Layout</span>
          </v-tooltip>
        </div>

        <!-- Display Settings - Always visible -->
        <div class="group-section">
          <v-tooltip right :open-delay="300" :disabled="isRailExpanded">
            <template v-slot:activator="{ on, attrs }">
              <div
                v-bind="attrs"
                v-on="on"
                class="group-row"
                :class="{ 'expanded': isRailExpanded }"
                role="button"
                aria-label="Display Settings"
                @click="handleDisplaySettings"
              >
                <div class="group-icon">
                  <v-icon :size="24">mdi-monitor-screenshot</v-icon>
                </div>
                <span v-if="isRailExpanded" class="group-title">Display Settings</span>
              </div>
            </template>
            <span>Display Settings</span>
          </v-tooltip>
        </div>

        <!-- Facility Admin - Requires Admin.Access permission -->
        <div v-if="canAccessFacilityAdmin" class="group-section">
          <v-tooltip right :open-delay="300" :disabled="isRailExpanded">
            <template v-slot:activator="{ on, attrs }">
              <div
                v-bind="attrs"
                v-on="on"
                class="group-row"
                :class="{ 'expanded': isRailExpanded }"
                role="button"
                aria-label="Facility Admin"
                @click="handleFacilityAdmin"
              >
                <div class="group-icon">
                  <v-icon :size="24">mdi-shield-crown-outline</v-icon>
                </div>
                <span v-if="isRailExpanded" class="group-title">Facility Admin</span>
              </div>
            </template>
            <span>Facility Admin</span>
          </v-tooltip>
        </div>

        <!-- Quick Settings - Always visible on facility views -->
        <div class="group-section">
          <v-tooltip right :open-delay="300" :disabled="isRailExpanded">
            <template v-slot:activator="{ on, attrs }">
              <div
                v-bind="attrs"
                v-on="on"
                class="group-row"
                :class="{ 'expanded': isRailExpanded, 'is-active': isQuickSettingsVisible }"
                role="button"
                aria-label="Quick Settings"
                @click="handleQuickSettings"
              >
                <div class="group-icon">
                  <v-icon :size="24">mdi-cog-outline</v-icon>
                </div>
                <span v-if="isRailExpanded" class="group-title">Quick Settings</span>
              </div>
            </template>
            <span>Quick Settings</span>
          </v-tooltip>
        </div>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script>
import { mapGetters } from 'vuex';
import { getGroupsSorted, getItemsForGroup, getGroupById } from '@Share/config/menuConfig';
import { PANEL_IDS } from '@/constants/panelIds';
import ViewerDisplayOptions from '@Share/components/ViewerDisplayOptions.vue';
import { $has } from '@/utils';
import permissions from '@Share/config/permissions';
import { isAnyViewerOpen } from '@/scripts/js/forgeviewer';

export default {
  name: 'ExpandableRail',

  components: {
    ViewerDisplayOptions,
  },

  data() {
    return {
      focusedIndex: 0,
      // History context menu state (right-click on Smart Back)
      showHistoryMenu: false,
      historyMenuX: 0,
      historyMenuY: 0,
    };
  },

  computed: {
    ...mapGetters('GENERAL', {
      isRailExpanded: 'GET_IS_RAIL_EXPANDED',
      activeGroup: 'GET_ACTIVE_GROUP',
      railWidthValue: 'GET_RAIL_WIDTH',
      hasPanels: 'GET_HAS_PANELS',
      panels: 'GET_PANELS',
      isQuickSettingsVisible: 'GET_IS_QUICK_SETTINGS_VISIBLE',
    }),

    ...mapGetters('VIEWERS_MODE', {
      isViewerModeActive: 'IS_AVAILABLE',
      viewerModeOptions: 'OPTIONS',
    }),

    // Bidirectional navigation history getters
    ...mapGetters('NAVIGATION_HISTORY', {
      canGoBack: 'CAN_GO_BACK',
      canGoForward: 'CAN_GO_FORWARD',
      backLabel: 'BACK_LABEL',
      forwardLabel: 'FORWARD_LABEL',
      backIsViewer: 'BACK_IS_VIEWER',
      backIsNonViewer: 'BACK_IS_NON_VIEWER',
      // Context menu getter (right-click history)
      backHistoryEntries: 'BACK_HISTORY_ENTRIES',
    }),

    ...mapGetters({
      isModelLoaded: 'GET_PAGE_WITH_TYPE_3D',
      taskBoards: 'TASK_MANAGER/GET_ENTRIES_BY_USER',
      registers: 'REGISTERS/GET_REGISTERS',
      storageLimits: 'BLOB_STORAGE/GET_FACILITY_LIMITS',
    }),

    /**
     * Computed rail width for the drawer
     */
    railWidth() {
      return this.isRailExpanded ? 240 : 60;
    },

    /**
     * Inline styles for positioning - OVERLAY behavior when expanded
     */
    railStyle() {
      return {
        top: 'var(--ds-nav-top-bar-height)',
        height: 'calc(100vh - var(--ds-nav-top-bar-height))',
        zIndex: 100, // High z-index for overlay
        position: 'fixed',
      };
    },

    /**
     * Main groups (position: 'main'), sorted by order
     */
    mainGroups() {
      return getGroupsSorted('main');
    },

    /**
     * Main groups with at least one visible item (position: 'main')
     * Filters out groups where all items are hidden by permissions
     */
    visibleMainGroups() {
      return this.mainGroups.filter(group => {
        // Groups with directPanel are clickable directly (e.g., Global Search) - always show
        if (group.directPanel) {
          return true;
        }
        // Check if group has any visible items after permission filtering
        const visibleItems = this.getGroupItems(group.id);
        return visibleItems.length > 0;
      });
    },

    /**
     * Footer groups (position: 'footer'), typically just Settings
     */
    footerGroups() {
      return getGroupsSorted('footer');
    },

    /**
     * All groups combined for keyboard navigation
     * Uses visibleMainGroups to only include groups with accessible items
     */
    allGroups() {
      return [...this.visibleMainGroups, ...this.footerGroups];
    },

    /**
     * Check if user has Admin.Access permission for Facility Admin button
     * Hides the Facility Admin footer button for users without admin access
     */
    canAccessFacilityAdmin() {
      return $has(permissions.Admin.Access);
    },

    /**
     * Detect if we're on MainView (Viewer) vs Dashboard/Task views
     * MainView = The primary viewer interface for 3D models, point clouds, 360° panoramas
     * Uses route-only detection for reliability (no timing issues with store state)
     */
    isOnMainView() {
      const routeName = this.$route.name;
      // MainView (Viewer) routes: 'facility' or 'RealityViewer'
      return routeName === 'facility' || routeName === 'RealityViewer';
    },

    /**
     * Dynamic label for "Back to Last Location" button
     * Comes from VIEWERS_MODE options set when user entered fullscreen viewer
     * Examples: "Back to Dashboard", "Back to Kanban", "Back to Task Board"
     */
    backToLocationLabel() {
      return this.viewerModeOptions?.button || 'Back to Previous';
    },

    isInPointCloudFullscreen() {
      return !!this.$store.state.savedViewConfig;
    },

    // ─────────────────────────────────────────────────────────────
    // Smart Navigation System - Computed Properties
    // Smart Back follows history + Legacy "Back to Viewer" fallback
    // ─────────────────────────────────────────────────────────────

    /**
     * Show the Smart Back button when:
     * - There IS history to go back to (canGoBack)
     * - AND NOT in VIEWERS_MODE (those have their own buttons)
     *
     * The button label is dynamic based on the history entry.
     * e.g., "Back to Viewer", "Back to Tags Register", "Back to TaskBoard"
     */
    showSmartBack() {
      return this.canGoBack && !this.isViewerModeActive;
    },

    /**
     * Determines if any context navigation button should be shown
     * Used to conditionally render the .context-nav-section wrapper
     * This section appears ABOVE the main navigation groups for prominence
     */
    showAnyContextNav() {
      // Smart Back (when we have history and not in VIEWERS_MODE)
      if (this.showSmartBack) return true;
      // Back to Viewer fallback (on non-MainView without history to viewer)
      if (!this.isOnMainView && !this.isViewerModeActive && !this.backGoesToViewer) return true;
      // VIEWERS_MODE buttons (Back to Last Location + End Mode)
      if (this.isOnMainView && this.isViewerModeActive) return true;
      return false;
    },

    /**
     * Check if Smart Back button navigates to Viewer
     * Used to determine when to show the legacy "Back to Viewer" button
     *
     * True when: Smart Back is visible AND its destination is the Viewer
     * False when: No history OR history points to non-viewer route
     *
     * Button visibility matrix:
     * | Scenario                        | Smart Back           | Back to Viewer  |
     * |---------------------------------|----------------------|-----------------|
     * | On Viewer                       | Hidden               | Hidden          |
     * | Non-Viewer, back→Viewer         | "Facility" (green)   | Hidden          |
     * | Non-Viewer, back→Non-Viewer     | "[Previous]" (purple)| Visible         |
     * | Non-Viewer, no history          | Hidden               | Visible         |
     */
    backGoesToViewer() {
      return this.showSmartBack && this.backIsViewer;
    },

    // DEPRECATED: Keep for backwards compatibility during transition
    // TODO: Remove these after verifying new system works
    showHistoryBackToViewer() {
      return false; // Replaced by showSmartBack
    },
    showHistoryBackToNonViewer() {
      return false; // Replaced by showSmartBack
    },
  },

  methods: {
    /**
     * Get menu items for a group
     * Filters items by permission requirements (hides items user doesn't have access to)
     */
    getGroupItems(groupId) {
      return getItemsForGroup(groupId).filter((item) => {
        // Filter by permission - hide items user doesn't have access to
        // This prevents showing navigation items that would lead to 403 errors
        if (item.permission) {
          // Permissions are defined as arrays - user needs ANY of them (OR logic)
          // e.g., Tags requires any of: Container.View, Equipment.View, etc.
          const hasPermission = $has(item.permission, true);
          if (!hasPermission) {
            return false;
          }
        }

        return true;
      });
    },

    /**
     * Check if an item is currently active (has open panel)
     */
    isItemActive(itemId) {
      return this.panels.some((p) => p.id === itemId);
    },

    /**
     * Check if item should be disabled
     */
    isItemDisabled(item) {
      if (item.requiresModel && !this.checkModelLoaded()) {
        return true;
      }
      if (item.id === 'scan-download') {
        if (!this.storageLimits?.canDownload) {
          return true;
        }
      }
      return false;
    },

    /**
     * Check if a model is loaded
     */
    checkModelLoaded() {
      return (
        this.$store.getters['GET_PAGE_WITH_TYPE_3D'] &&
        !this.$store.state.viewerState?.isLoading
      );
    },

    /**
     * Get submenu items for dynamic items (Task Boards, Registers)
     */
    getSubmenuItems(item) {
      if (item.type !== 'submenu') {
        return null;
      }
      if (item.id === 'workspaces') {
        return this.taskBoards || [];
      }
      if (item.id === 'registers') {
        return this.registers || [];
      }
      return [];
    },

    /**
     * Navigate back to facilities page
     * Shows warning if any viewer is open
     */
    navigateToFacilities() {
      if (isAnyViewerOpen()) {
        this.$dialog.warning({
          text: 'You have a viewer open. Navigating away will close it. Are you sure you want to continue?',
          title: 'Close Viewer',
          actions: {
            false: 'Cancel',
            true: {
              text: 'Continue',
              color: 'primary',
              handle: () => {
                this.$store.dispatch('GENERAL/CLOSE_ALL_PANELS');
                this.$store.dispatch('GENERAL/COLLAPSE_RAIL');
                this.$router.push('/');
              },
            },
          },
        });
      } else {
        // Close all panels and collapse rail
        this.$store.dispatch('GENERAL/CLOSE_ALL_PANELS');
        this.$store.dispatch('GENERAL/COLLAPSE_RAIL');
        // Navigate to facilities page
        this.$router.push('/');
      }
    },

    /**
     * Handle mouse leave - always collapse rail (panels remain visible)
     */
    handleMouseLeave() {
      if (this.isRailExpanded) {
        this.$store.dispatch('GENERAL/COLLAPSE_RAIL');
      }
    },

    /**
     * Handle group icon click
     * Groups with `directPanel` property open their panel directly without expanding the rail
     */
    handleGroupClick(groupId) {
      const group = getGroupById(groupId);

      // Check if group has directPanel configuration
      if (group?.directPanel) {
        // Open panel directly, skip rail expansion
        this.$store.dispatch('GENERAL/OPEN_PANEL', group.directPanel);
        // Mark this group as active for visual feedback
        this.$store.commit('GENERAL/SET_ACTIVE_GROUP', groupId);
        this.$emit('group-click', { groupId, directPanel: true });
        return;
      }

      // Standard toggle behavior for normal groups
      this.$store.dispatch('GENERAL/TOGGLE_GROUP', groupId);
      this.$emit('group-click', { groupId });
    },

    /**
     * Handle menu item click
     */
    handleItemClick({ itemId, item }) {
      if (item.type === 'panel') {
        this.$store.dispatch('GENERAL/OPEN_PANEL', itemId);
        this.$store.dispatch('GENERAL/COLLAPSE_RAIL');
      } else if (item.type === 'route') {
        const facilityId = this.$route.params.id;
        const route = `/${facilityId}${item.routePath}`;
        this.$router.push(route);
        this.$store.dispatch('GENERAL/COLLAPSE_RAIL');
        this.$store.dispatch('GENERAL/CLOSE_ALL_PANELS');
      }
      this.$emit('item-click', { itemId, type: item.type });
    },

    /**
     * Handle submenu item click
     */
    handleSubmenuClick(payload) {
      this.$store.dispatch('GENERAL/COLLAPSE_RAIL');
      this.$emit('submenu-click', payload);
    },

    /**
     * Handle Quick Settings button click
     * Toggles the floating Quick Settings UI visibility
     */
    handleQuickSettings() {
      const isCurrentlyVisible = this.$store.getters['GENERAL/GET_IS_QUICK_SETTINGS_VISIBLE'];
      this.$store.commit('GENERAL/SET_QUICK_SETTINGS_VISIBLE', !isCurrentlyVisible);
    },

    /**
     * Handle Display Settings button click
     * Opens the display settings panel
     */
    handleDisplaySettings() {
      this.$store.dispatch('GENERAL/OPEN_PANEL', PANEL_IDS.DISPLAY_SETTINGS);
    },

    /**
     * Handle Facility Admin button click
     * Navigates to the facility admin page
     * Shows warning if any viewer is open
     */
    handleFacilityAdmin() {
      const facilityId = this.$route.params.id;
      if (isAnyViewerOpen()) {
        this.$dialog.warning({
          text: 'You have a viewer open. Navigating away will close it. Are you sure you want to continue?',
          title: 'Close Viewer',
          actions: {
            false: 'Cancel',
            true: {
              text: 'Continue',
              color: 'primary',
              handle: () => {
                this.$router.push(`/${facilityId}/admin`);
                this.$store.dispatch('GENERAL/COLLAPSE_RAIL');
                this.$store.dispatch('GENERAL/CLOSE_ALL_PANELS');
              },
            },
          },
        });
      } else {
        this.$router.push(`/${facilityId}/admin`);
        this.$store.dispatch('GENERAL/COLLAPSE_RAIL');
        this.$store.dispatch('GENERAL/CLOSE_ALL_PANELS');
      }
    },

    /**
     * Navigate back to MainView (3D/360 Viewer)
     * Used when on Dashboard or Task views
     */
    navigateToMainView() {
      const facilityId = this.$route.params.id;
      if (facilityId) {
        this.$router.push(`/${facilityId}`);
        this.$store.dispatch('GENERAL/COLLAPSE_RAIL');
        this.$store.dispatch('GENERAL/CLOSE_ALL_PANELS');
      }
    },

    /**
     * Navigate back to the last location (Dashboard/Task Board/Kanban/etc.)
     * Uses VIEWERS_MODE store to return to the saved route from before fullscreen viewer
     * This replaces the floating mode-options buttons previously in pageView.vue
     */
    navigateToLastLocation() {
      this.$store.dispatch('VIEWERS_MODE/BACK_TO_PAGE');
      this.$store.dispatch('GENERAL/COLLAPSE_RAIL');
      this.$store.dispatch('GENERAL/CLOSE_ALL_PANELS');
    },

    /**
     * Exit viewer mode without navigating back
     * Clears the VIEWERS_MODE state but keeps user on current viewer
     * Useful when user wants to stay in viewer but clear the mode context
     * (e.g., finished their Dashboard task but want to continue exploring)
     */
    exitViewerMode() {
      this.$store.dispatch('VIEWERS_MODE/EXIT_VIEWER_MODE');
      this.$store.dispatch('GENERAL/COLLAPSE_RAIL');
    },

    exitPointCloudFullscreen() {
      const mainApp = this.$store.state.mainApp;
      const saved = this.$store.state.savedViewConfig;
      if (mainApp && saved) {
        // Restore visibility — 360 viewer alive (was only hidden via v-show)
        [1, 2, 3, 4].forEach(i => {
          mainApp[`isView${i}Visible`] = saved[`view${i}Visible`];
        });
        // Close any 3D slot that was opened by us
        [1, 2, 3, 4].forEach(i => {
          if (mainApp[`view${i}Type`] === '3d' && !saved[`view${i}Visible`] && saved[`view${i}Type`] !== '3d') {
            mainApp[`view${i}Type`] = '';
            mainApp[`isView${i}Visible`] = false;
          }
        });
      }

      const forgeService = this.$store.state.forgeService;
      if (forgeService) {
        forgeService.linkViewers();
        const cropExt = forgeService.getCropping3DExtension();
        if (cropExt?.croppingOn) {
          cropExt.toggleCropbox(false);
        }
      }

      this.$store.commit('SET_CROP_FROM_NEARBY_SELECTOR', false);
    },

    /**
     * Navigate back using the bidirectional navigation history
     * Uses the NAVIGATION_HISTORY module for browser-style back navigation
     * Shows warning if any viewer is open
     */
    navigateHistoryBack() {
      if (isAnyViewerOpen()) {
        this.$dialog.warning({
          text: 'You have a viewer open. Navigating away will close it. Are you sure you want to continue?',
          title: 'Close Viewer',
          actions: {
            false: 'Cancel',
            true: {
              text: 'Continue',
              color: 'primary',
              handle: () => {
                this.$store.dispatch('NAVIGATION_HISTORY/GO_BACK');
              },
            },
          },
        });
      } else {
        this.$store.dispatch('NAVIGATION_HISTORY/GO_BACK');
      }
    },

    // ─────────────────────────────────────────────────────────────
    // History Context Menu Methods (Right-Click on Smart Back)
    // ─────────────────────────────────────────────────────────────

    /**
     * Open history context menu on right-click
     * Shows last 5 history entries for direct navigation
     */
    openHistoryContextMenu(event) {
      // Guard: Only show if we have history entries to display
      if (!this.backHistoryEntries || this.backHistoryEntries.length === 0) {
        return;
      }

      this.historyMenuX = event.clientX;
      this.historyMenuY = event.clientY;
      this.showHistoryMenu = true;
    },

    /**
     * Navigate to specific history entry from context menu
     * Shows warning if any viewer is open
     * @param {Object} entry - Entry object with actualIndex property
     */
    navigateToHistoryEntry(entry) {
      if (isAnyViewerOpen()) {
        this.$dialog.warning({
          text: 'You have a viewer open. Navigating away will close it. Are you sure you want to continue?',
          title: 'Close Viewer',
          actions: {
            false: 'Cancel',
            true: {
              text: 'Continue',
              color: 'primary',
              handle: () => {
                this.$store.dispatch('NAVIGATION_HISTORY/GO_TO_INDEX', entry.actualIndex);
                this.showHistoryMenu = false;
              },
            },
          },
        });
      } else {
        this.$store.dispatch('NAVIGATION_HISTORY/GO_TO_INDEX', entry.actualIndex);
        this.showHistoryMenu = false;
      }
    },

    /**
     * Get icon for history entry based on module type and route name
     */
    getHistoryEntryIcon(entry) {
      // Viewer routes get cube icon
      if (entry.moduleType === 'VIEWER') return 'mdi-cube-scan';

      // Map route names to icons
      const iconMap = {
        'TagsRegisterView': 'mdi-clipboard-list-outline',
        'kanban': 'mdi-view-column',
        'gantt': 'mdi-chart-gantt',
        'schedule': 'mdi-calendar',
        'Dashboard': 'mdi-view-dashboard',
        'TaskBoard': 'mdi-clipboard-check-outline',
      };

      return iconMap[entry.routeName] || 'mdi-file-document-outline';
    },

    /**
     * Keyboard navigation handler
     */
    handleKeyDown(event, currentIndex) {
      const groups = this.allGroups;
      let newIndex = currentIndex;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          newIndex = Math.min(currentIndex + 1, groups.length - 1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          newIndex = Math.max(currentIndex - 1, 0);
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          this.handleGroupClick(groups[currentIndex].id);
          return;
        case 'Home':
          event.preventDefault();
          newIndex = 0;
          break;
        case 'End':
          event.preventDefault();
          newIndex = groups.length - 1;
          break;
        default:
          return;
      }

      if (newIndex !== currentIndex) {
        this.focusedIndex = newIndex;
        this.$nextTick(() => {
          const rows = this.$el.querySelectorAll('.group-row');
          if (rows[newIndex]) {
            rows[newIndex].focus();
          }
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.expandable-rail {
  // Override Vuetify defaults
  &.v-navigation-drawer {
    position: fixed !important;
    border-radius: 0;
    overflow: visible !important;
    transition: width var(--ds-transition-rail);
    box-shadow: 0px 6px 8px rgba(0, 0, 0, 0.3);

    .v-navigation-drawer__border {
      display: none !important;
    }
  }

  // Dark gradient background
  background: linear-gradient(180deg, #0a2626 0%, #061a1a 50%, #021a1a 100%) !important;

  .rail-content {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow-y: auto;
    overflow-x: hidden;

    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.2);
      border-radius: 2px;
    }
  }

  .groups-container {
    flex: 1;
    padding: var(--ds-space-sm) 0;
    padding-top: 0;
  }

  // ═══════════════════════════════════════════════════════════════════
  // Context Navigation Section
  // Smart Back + Legacy buttons positioned ABOVE main groups for prominence
  // ═══════════════════════════════════════════════════════════════════
  .context-nav-section {
    padding: var(--ds-space-sm) 0;
    border-bottom: 1px solid var(--ds-border-subtle);
    display: flex;
    flex-direction: column;
    gap: 4px;

    // Subtle visual differentiation - gradient background for prominence
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.03) 0%,
      transparent 100%
    );

    .group-section {
      margin-bottom: var(--ds-space-xs);

      &:last-child {
        margin-bottom: 0;
      }
    }

    // Override button margins - parent section now handles spacing
    .context-nav-btn {
      margin-top: 0;
      margin-bottom: 0;
    }

    .smart-nav-item {
      margin-top: 0;
      margin-bottom: 0;
    }
  }

  .footer-groups {
    border-top: 1px solid var(--ds-border-subtle);
  }

  // Footer action buttons row
  .footer-action-buttons {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: var(--ds-space-xs);
    padding: var(--ds-space-sm) var(--ds-space-xs);
    margin: var(--ds-space-xs);
    border-bottom: 1px solid var(--ds-border-subtle);
    overflow: visible; // Allow speed-dial to expand outside
    position: relative;
    z-index: 150;

    .action-btn-wrapper {
      display: flex;
      justify-content: center;
      align-items: center;
    }

    .action-btn {
      width: var(--ds-nav-icon-btn-size, 36px) !important;
      height: var(--ds-nav-icon-btn-size, 36px) !important;
      border-radius: var(--ds-radius-md);
      transition: all var(--ds-transition-fast);

      &:hover:not(:disabled) {
        background-color: var(--ds-surface-hover);
      }

      &:disabled {
        opacity: 0.4;
      }

      .v-icon {
        color: rgba(255, 255, 255, 0.6);
        transition: color var(--ds-transition-fast);
      }

      &:hover:not(:disabled) .v-icon {
        color: rgba(255, 255, 255, 0.9);
      }
    }
  }

  .group-section {
    margin-bottom: 2px;
  }

  // Group row - horizontal layout with icon + title
  .group-row {
    display: flex;
    align-items: center;
    padding: var(--ds-space-sm) var(--ds-space-sm);
    margin: 2px var(--ds-space-xs);
    border-radius: var(--ds-radius-md);
    cursor: pointer;
    transition: all var(--ds-transition-fast);
    min-height: 44px;

    &:hover {
      background-color: var(--ds-surface-hover);

      .group-icon .v-icon {
        color: rgba(255, 255, 255, 0.95);
      }
    }

    &:focus {
      outline: none;
    }

    &.active {
      background-color: var(--ds-surface-active);
      position: relative;

      .group-icon .v-icon {
        color: rgba(255, 255, 255, 0.95);
      }

      .group-title {
        color: rgba(255, 255, 255, 0.95);
        font-weight: 600;
      }

      // Left border indicator - teal accent bar
      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
        width: 3px;
        height: 24px;
        background-color: var(--v-primary-base);
        border-radius: 0 2px 2px 0;
      }
    }

    &.has-open-panel {
      background-color: rgba(3, 98, 76, 0.2);
    }

    &.is-active {
      background-color: rgba(3, 98, 76, 0.15);

      .v-icon {
        color: rgba(255, 255, 255, 0.95) !important;
      }

      .group-title {
        color: rgba(255, 255, 255, 0.95);
        font-weight: 600;
      }
    }

    &.expanded {
      padding-right: var(--ds-space-md);
    }
  }

  .group-icon {
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    position: relative;

    .v-icon {
      color: rgba(255, 255, 255, 0.7);
      transition: color var(--ds-transition-fast);
    }
  }

  .group-title {
    flex: 1;
    font-size: 13px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.9);
    margin-left: var(--ds-space-xs);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .expand-arrow {
    color: rgba(255, 255, 255, 0.5);
    transition: transform var(--ds-transition-fast);
    margin-left: auto;

    &.rotated {
      transform: rotate(180deg);
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  // Smart Navigation System Styles
  // Smart Back follows history + Legacy "Back to Viewer" fallback
  // ═══════════════════════════════════════════════════════════════════

  .smart-nav-section {
    margin-top: var(--ds-space-sm);
    margin-bottom: var(--ds-space-sm);
  }

  .smart-nav-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 2px var(--ds-space-xs);
    gap: var(--ds-space-xs);
  }

  // Smart Back Button - Primary navigation action
  .smart-back-btn {
    display: flex;
    align-items: center;
    // Don't use flex: 1 by default - causes zero-size when rail is collapsed
    // Only expand to fill space when rail is expanded (see &.expanded below)
    min-width: 0; // Allow text truncation
    padding: var(--ds-space-sm) var(--ds-space-sm);
    border-radius: var(--ds-radius-md);
    cursor: pointer;
    transition: all var(--ds-transition-fast);
    min-height: 44px;

    // When expanded, grow to fill space for label
    &.expanded {
      flex: 1;
    }

    .group-title {
      flex: 1;
      font-size: 13px;
      font-weight: 600;
      margin-left: var(--ds-space-xs);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 140px;
    }

    // Viewer destination styling (green/teal accent)
    &.is-viewer {
      background: linear-gradient(135deg, rgba(3, 98, 76, 0.2) 0%, rgba(90, 122, 56, 0.15) 100%);
      border: 1px solid rgba(3, 98, 76, 0.4);
      box-shadow: 0 0 8px rgba(3, 98, 76, 0.2);

      .group-icon .v-icon {
        animation: viewer-icon-pulse 2.5s ease-in-out infinite;
      }

      .group-title {
        color: rgba(255, 255, 255, 0.95);
      }

      &:hover {
        background: linear-gradient(135deg, rgba(3, 98, 76, 0.3) 0%, rgba(90, 122, 56, 0.25) 100%);
        border-color: rgba(3, 98, 76, 0.6);
        box-shadow:
          0 0 16px rgba(3, 98, 76, 0.3),
          0 0 24px rgba(3, 98, 76, 0.15);

        .group-icon .v-icon {
          animation: viewer-icon-pulse 1.5s ease-in-out infinite;
        }
      }
    }

    // Non-Viewer destination styling (purple accent)
    &.is-nonviewer {
      background: linear-gradient(135deg, rgba(156, 39, 176, 0.2) 0%, rgba(123, 31, 162, 0.15) 100%);
      border: 1px solid rgba(156, 39, 176, 0.4);
      box-shadow: 0 0 8px rgba(156, 39, 176, 0.2);

      .group-icon .v-icon {
        color: #AB47BC !important;
      }

      .group-title {
        color: #AB47BC;
      }

      &:hover {
        background: linear-gradient(135deg, rgba(156, 39, 176, 0.3) 0%, rgba(123, 31, 162, 0.25) 100%);
        border-color: rgba(156, 39, 176, 0.6);
        box-shadow:
          0 0 16px rgba(156, 39, 176, 0.3),
          0 0 24px rgba(156, 39, 176, 0.15);

        .group-icon .v-icon {
          animation: nonviewer-icon-pulse 1.5s ease-in-out infinite;
        }
      }
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  // Legacy Button Styles (kept for fallback and VIEWERS_MODE buttons)
  // ═══════════════════════════════════════════════════════════════════

  // Back to Viewer button - Prominent accent styling
  .back-to-viewer-btn {
    background: linear-gradient(135deg, rgba(3, 98, 76, 0.2) 0%, rgba(90, 122, 56, 0.15) 100%);
    border: 1px solid rgba(3, 98, 76, 0.4);
    box-shadow: 0 0 8px rgba(3, 98, 76, 0.2);

    // Margins only when NOT in context-nav-section (i.e., if ever used elsewhere)
    &:not(.context-nav-btn) {
      margin-top: var(--ds-space-sm);
      margin-bottom: var(--ds-space-sm);
    }

    .group-icon .v-icon {
        animation: viewer-icon-pulse 2.5s ease-in-out infinite;
    }

    .group-title {
      color: rgba(255, 255, 255, 0.95);
      font-weight: 600;
    }

    &:hover {
      background: linear-gradient(135deg, rgba(3, 98, 76, 0.3) 0%, rgba(90, 122, 56, 0.25) 100%);
      border-color: rgba(3, 98, 76, 0.6);
      box-shadow:
        0 0 16px rgba(3, 98, 76, 0.3),
        0 0 24px rgba(3, 98, 76, 0.15);

      .group-icon .v-icon {
        animation: viewer-icon-pulse 1.5s ease-in-out infinite;
      }
    }
  }

  @keyframes viewer-icon-pulse {
    0%, 100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.15);
    }
  }

  // Back to Non-Viewer button - Purple accent styling (for bidirectional navigation)
  // Shows on Viewer when user can go back to Register/Dashboard they came from
  .back-to-nonviewer-btn {
    background: linear-gradient(135deg, rgba(156, 39, 176, 0.2) 0%, rgba(123, 31, 162, 0.15) 100%);
    border: 1px solid rgba(156, 39, 176, 0.4);
    box-shadow: 0 0 8px rgba(156, 39, 176, 0.2);
    margin-top: var(--ds-space-sm);
    margin-bottom: var(--ds-space-sm);

    .group-icon .v-icon {
      color: #AB47BC !important;
    }

    .group-title {
      color: #AB47BC;
      font-weight: 600;
    }

    &:hover {
      background: linear-gradient(135deg, rgba(156, 39, 176, 0.3) 0%, rgba(123, 31, 162, 0.25) 100%);
      border-color: rgba(156, 39, 176, 0.6);
      box-shadow:
        0 0 16px rgba(156, 39, 176, 0.3),
        0 0 24px rgba(156, 39, 176, 0.15);

      .group-icon .v-icon {
        animation: nonviewer-icon-pulse 1.5s ease-in-out infinite;
      }
    }
  }

  @keyframes nonviewer-icon-pulse {
    0%, 100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.15);
    }
  }

  // Back to Last Location button - Blue accent styling (for returning from fullscreen viewer)
  .back-to-location-btn {
    background: linear-gradient(135deg, rgba(66, 165, 245, 0.2) 0%, rgba(33, 150, 243, 0.15) 100%);
    border: 1px solid rgba(66, 165, 245, 0.4);
    box-shadow: 0 0 8px rgba(66, 165, 245, 0.2);

    // Margins only when NOT in context-nav-section
    &:not(.context-nav-btn) {
      margin-top: var(--ds-space-sm);
      margin-bottom: var(--ds-space-sm);
    }

    .group-icon .v-icon {
      color: #42A5F5 !important;
    }

    .group-title {
      color: #42A5F5;
      font-weight: 600;
    }

    &:hover {
      background: linear-gradient(135deg, rgba(66, 165, 245, 0.3) 0%, rgba(33, 150, 243, 0.25) 100%);
      border-color: rgba(66, 165, 245, 0.6);
      box-shadow:
        0 0 16px rgba(66, 165, 245, 0.3),
        0 0 24px rgba(66, 165, 245, 0.15);

      .group-icon .v-icon {
        animation: location-icon-pulse 1.5s ease-in-out infinite;
      }
    }
  }

  @keyframes location-icon-pulse {
    0%, 100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.15);
    }
  }

  // End Mode button - Amber/Yellow accent styling (exit without navigating)
  .end-mode-btn {
    background: linear-gradient(135deg, rgba(255, 193, 7, 0.15) 0%, rgba(255, 160, 0, 0.1) 100%);
    border: 1px solid rgba(255, 193, 7, 0.35);
    box-shadow: 0 0 6px rgba(255, 193, 7, 0.15);

    // Margins only when NOT in context-nav-section
    &:not(.context-nav-btn) {
      margin-bottom: var(--ds-space-sm);
    }

    .group-icon .v-icon {
      color: #FFC107 !important;
    }

    .group-title {
      color: #FFC107;
      font-weight: 500;
    }

    &:hover {
      background: linear-gradient(135deg, rgba(255, 193, 7, 0.25) 0%, rgba(255, 160, 0, 0.2) 100%);
      border-color: rgba(255, 193, 7, 0.5);
      box-shadow:
        0 0 12px rgba(255, 193, 7, 0.25),
        0 0 20px rgba(255, 193, 7, 0.1);

      .group-icon .v-icon {
        animation: end-mode-icon-pulse 1.5s ease-in-out infinite;
      }
    }
  }

  @keyframes end-mode-icon-pulse {
    0%, 100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.15);
    }
  }

  // Menu items
  .menu-items {
    padding: var(--ds-space-xs) 0;
    margin-left: 32px;
    overflow: hidden;
  }

  .menu-item {
    display: flex;
    align-items: center;
    padding: var(--ds-space-sm) var(--ds-space-md);
    margin: 2px var(--ds-space-xs);
    border-radius: var(--ds-radius-sm);
    cursor: pointer;
    transition: all var(--ds-transition-fast);
    min-height: 40px;

    &:hover:not(.disabled) {
      background-color: var(--ds-surface-hover);
    }

    &.active {
      background-color: var(--ds-surface-active);
      position: relative;

      .menu-item-title {
        color: rgba(255, 255, 255, 0.95);
        font-weight: 600;
      }

      // Left accent bar for menu items
      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
        width: 2px;
        height: 20px;
        background-color: var(--v-primary-base);
        border-radius: 0 2px 2px 0;
      }
    }

    &.disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }

  .menu-item-icon {
    color: rgba(255, 255, 255, 0.7);
    margin-right: var(--ds-space-sm);
    transition: color var(--ds-transition-fast);
  }

  .menu-item-title {
    font-size: 13px;
    font-weight: 400;
    color: rgba(255, 255, 255, 0.85);
  }

  // Menu expansion animation
  .menu-expand-enter-active,
  .menu-expand-leave-active {
    transition: all var(--ds-transition-rail);
    max-height: 300px;
  }

  .menu-expand-enter,
  .menu-expand-leave-to {
    opacity: 0;
    max-height: 0;
  }
}

// History Context Menu (right-click on Smart Back)
// Positioned as overlay via v-menu absolute positioning
.history-context-menu {
  background: linear-gradient(180deg, #0a2626 0%, #021a1a 100%) !important;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: var(--ds-radius-md);
  min-width: 180px;
  max-width: 260px;
  padding: 4px 0;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);

  .v-list-item {
    min-height: 36px;
    padding: 0 12px;
    cursor: pointer;
    transition: background-color var(--ds-transition-fast);

    &:hover {
      background-color: var(--ds-surface-hover);
    }
  }

  .v-list-item-icon {
    margin-right: 8px;
    min-width: 20px;
    align-self: center;

    .v-icon {
      color: rgba(255, 255, 255, 0.7);
      font-size: 18px;
    }
  }

  .v-list-item-title {
    font-size: 13px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.9);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

// Mobile responsive
@media (max-width: 768px) {
  .expandable-rail {
    .group-row {
      min-height: 48px;
    }

    .group-icon {
      width: 44px;
      height: 44px;
    }
  }
}
</style>
